import type {
	VsmContext,
	PageConfigEntry,
	MasterPageConfigEntry,
	PageConfigFn,
	MasterPageConfigFn,
} from '@wix/thunderbolt-vsm-api-types'
import type {
	PageAnchorsPageConfig,
	PageAnchorsMasterPageConfig,
	PageConfigAnchor,
	PageAnchorsObserverComp,
	ActiveAnchorObserverComp,
} from '../types'
import type { AnchorData, VerticalAnchorsMenuData } from '@wix/thunderbolt-components-native'
import { isDisplayedOnly } from '@wix/thunderbolt-commons'
import { name } from '../symbols'
import { ACTIVE_ANCHOR_OBSERVERS_TYPES, PAGE_ANCHORS_OBSERVERS_TYPES } from '../componentTypes'

const TOP_AND_BOTTOM_ANCHORS: Record<string, string> = {
	SCROLL_TO_TOP: 'top of page',
	SCROLL_TO_BOTTOM: 'bottom of page',
}

function getFilteredAnchors(api: VsmContext['api'], pageId: string): PageConfigAnchor[] {
	const anchorsMap = getAllAnchorsInPage(api, pageId)
	const siteFooterLayout = api.components.getCompLayout('SITE_FOOTER')
	const isFooterFrozen = siteFooterLayout?.fixedPosition === true
	return Object.values(anchorsMap).filter((anchor) => !(anchor.compId === 'SITE_FOOTER' && isFooterFrozen))
}

function buildPageAnchorsObservers(api: VsmContext['api']): PageAnchorsObserverComp[] {
	const observers: PageAnchorsObserverComp[] = []
	for (const observerType of PAGE_ANCHORS_OBSERVERS_TYPES) {
		const comps = api.components.getComponentsByType(observerType)
		for (const [compId, comp] of Object.entries(comps)) {
			const compData = api.components.getCompData<VerticalAnchorsMenuData>(compId)
			observers.push({
				id: compId,
				type: comp.componentType,
				compData: {
					pageTopLabel: compData?.pageTopLabel ?? null,
					hiddenAnchorIds: compData?.hiddenAnchorIds ?? null,
				},
			})
		}
	}
	return observers
}

function buildActiveAnchorObservers(api: VsmContext['api']): ActiveAnchorObserverComp[] {
	const observers: ActiveAnchorObserverComp[] = []
	for (const observerType of ACTIVE_ANCHOR_OBSERVERS_TYPES) {
		const comps = api.components.getComponentsByType(observerType)
		for (const [compId, comp] of Object.entries(comps)) {
			observers.push({ id: compId, type: comp.componentType })
		}
	}
	return observers
}

function getAllAnchorsInPage(api: VsmContext['api'], targetPageId: string): Record<string, PageConfigAnchor> {
	const anchors: Record<string, PageConfigAnchor> = {}

	const anchorComps = api.components.getComponentsByType('Anchor')
	for (const [compId] of Object.entries(anchorComps)) {
		if (isDisplayedOnly(compId)) continue
		if (api.components.getComponentPageId(compId) !== targetPageId) continue
		const anchorData = api.components.getCompData<AnchorData>(compId)
		if (!anchorData) continue
		const anchorId = anchorData.id ?? compId
		const anchorCompId = anchorData.compId ?? compId
		const layout = api.components.getCompLayout(anchorCompId)
		const anchor: PageConfigAnchor = {
			compId: anchorCompId,
			dataId: anchorId,
			// carmi parity: anchorData.name may be undefined at runtime, but carmi's chain
			// types claim `string` and silently pass undefined through. Match that here.
			name: TOP_AND_BOTTOM_ANCHORS[compId] ?? (anchorData.name as string),
		}
		if (layout?.y !== undefined) anchor.top = layout.y
		anchors[anchorId] = anchor
	}

	const compsWithAnchor = api.components.getComponentsByPredicate((_, cId) => {
		return api.components.getCompAnchor(cId) !== undefined
	})
	for (const [compId, comp] of Object.entries(compsWithAnchor)) {
		if (isDisplayedOnly(compId)) continue
		if (api.components.getComponentPageId(compId) !== targetPageId) continue
		const anchorData = api.components.getCompAnchor(compId)
		if (!anchorData) continue
		const anchorKey = comp.anchorQuery ?? compId
		const anchor: PageConfigAnchor = {
			dataId: comp.dataQuery ?? comp.anchorQuery ?? compId,
			compId,
			// carmi parity: see note in getAnchorsFromData above.
			name: anchorData.name as string,
		}
		anchors[anchorKey] = anchor
	}

	return anchors
}

const pageAnchorsPageConfig: PageConfigFn<PageAnchorsPageConfig> = ({ api }: VsmContext, pageId: string) => {
	const anchors = getFilteredAnchors(api, pageId)

	if (anchors.length === 0) {
		return { shouldLoadPageFeature: false }
	}

	const pageAnchorsObservers = buildPageAnchorsObservers(api)
	const activeAnchorObservers = buildActiveAnchorObservers(api)

	const headerSections = api.components.getComponentsByType('HeaderSection')
	const headerSectionEntries = Object.entries(headerSections)
	const headerSectionId = headerSectionEntries.length > 0 ? headerSectionEntries[0][0] : null
	let headerComponentId: string | null = null
	if (headerSectionId) {
		headerComponentId = api.components.getCompParent(headerSectionId) ?? null
	}

	const isBuilderComponentModel = api.env.getIsBuilderComponentModel()

	return {
		shouldLoadPageFeature: true,
		pageConfig: {
			anchors,
			pageAnchorsObservers,
			activeAnchorObservers,
			headerComponentId: headerComponentId!,
			isBuilderComponentModel,
		},
	}
}

const pageAnchorsMasterPageConfig: MasterPageConfigFn<PageAnchorsMasterPageConfig> = ({ api }: VsmContext) => {
	const pageId = api.page.getCurrentPageId()!
	const anchors = getFilteredAnchors(api, pageId)
	const pageAnchorsObservers = buildPageAnchorsObservers(api)
	const activeAnchorObservers = buildActiveAnchorObservers(api)

	const siteHeaderLayout = api.components.getCompLayout('SITE_HEADER')
	const siteOffset = siteHeaderLayout?.fixedPosition ? (siteHeaderLayout.height ?? 0) : 0

	return {
		anchors,
		pageAnchorsObservers,
		activeAnchorObservers,
		siteOffset,
	}
}

export const pageConfig: PageConfigEntry<PageAnchorsPageConfig> = {
	name,
	configFactory: pageAnchorsPageConfig,
	experiment: 'specs.thunderbolt.vsmPageConfigs',
}

export const masterPageConfig: MasterPageConfigEntry<PageAnchorsMasterPageConfig> = {
	name,
	configFactory: pageAnchorsMasterPageConfig,
	experiment: 'specs.thunderbolt.vsmPageConfigs',
}
